import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { FakeButtonLink } from '../../components/FakeLink';
import { baseGetRowClassName, baseGridSx } from '../../components/Table/gridStyles';
import { useNow, usePermissions, useSearchParamsState } from '../../hooks';
import { useFetchAccountDeletionRequestsQuery } from '../../redux/features/accountDeletion/accountDeletion.apiSlice';
import { locations } from '../../routes/locations';
import { AccountDeletionRequest, AccountDeletionStatus } from '../../types/accountDeletion';
import { formatDateTime } from '../../utils/date';
import * as permissions from '../../utils/permissions';
import { AccountDeletionDialog } from './AccountDeletionDialog';
import { QUEUE_VIEWS, reviewCountdown, statusInfo } from './accountDeletion';

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = '25';

/**
 * The clock reaches the cells through context, so the grid's columns stay one stable array: a new array every minute
 * would reset the widths and order the employee set.
 */
const NowContext = React.createContext<Date>(new Date());

function StatusCell({ row }: { row: AccountDeletionRequest }) {
  const { label, color } = statusInfo(row, useContext(NowContext));

  return <Chip size="small" color={color} label={label} />;
}

function ReviewCell({ row }: { row: AccountDeletionRequest }) {
  const now = useContext(NowContext);

  if (row.status !== AccountDeletionStatus.SUBMITTED) {
    return <>{formatDateTime(row.reviewEndsAt)}</>;
  }

  return (
    <Box>
      <div>{reviewCountdown(row.reviewEndsAt, now)}</div>
      <Typography variant="caption" color="text.secondary">
        {formatDateTime(row.reviewEndsAt)}
      </Typography>
    </Box>
  );
}

/**
 * Account deletion requests (MAD-5179). Users ask in the app; each request is reviewed while the account is frozen.
 * Support can cancel it until Ops starts it; Ops then runs the delete-user runbook and marks it completed.
 */
export function AccountDeletionList() {
  // Re-read every minute and whenever data arrives: a list fetched just as a review ends must not show "ready" with
  // Start still disabled.
  const [now, refreshNow] = useNow();
  const canCancel = usePermissions({ items: [permissions.AccountDeletion.cancel] });
  const canProcess = usePermissions({ items: [permissions.AccountDeletion.process] });
  // The tab and page live in the URL, so a refresh or a shared link opens the same view.
  const [viewKey] = useSearchParamsState('tab', QUEUE_VIEWS[0].key);
  const [page] = useSearchParamsState('page', '0');
  const [pageSize] = useSearchParamsState('pageSize', DEFAULT_PAGE_SIZE);
  const [, setSearchParams] = useSearchParams();
  const paginationModel: GridPaginationModel = { page: Number(page) || 0, pageSize: Number(pageSize) || 25 };
  // The opened request, kept as last seen: after a refused action the refetch may move it out of the current tab, and
  // the dialog must stay to show why.
  const [opened, setOpened] = useState<AccountDeletionRequest | null>(null);

  const view = QUEUE_VIEWS.find(({ key }) => key === viewKey) || QUEUE_VIEWS[0];
  const { data, isFetching, isError } = useFetchAccountDeletionRequestsQuery({
    ...view.args,
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
  });
  const selected = (opened && data?.items.find(({ id }) => id === opened.id)) || opened;

  useEffect(refreshNow, [data, refreshNow]);

  // Each of these changes two params at once. useSearchParamsState's setter rebuilds the URL from the params it saw at
  // render, so two of them in one event would undo each other: one merged update instead.
  const updateParams = (patch: Record<string, string>) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);

        Object.entries(patch).forEach(([key, value]) => next.set(key, value));

        return next;
      },
      { replace: true },
    );

  const onViewChange = (_: React.SyntheticEvent, key: string) => updateParams({ tab: key, page: '0' });

  const onPaginationModelChange = (model: GridPaginationModel) =>
    updateParams({ page: String(model.page), pageSize: String(model.pageSize) });

  // madkhol-user sorts the queue oldest first and filters by status only, so the grid offers neither sorting nor
  // column filters.
  const columns = useMemo<GridColDef<AccountDeletionRequest>[]>(() => {
    const base: GridColDef<AccountDeletionRequest>[] = [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Open',
        width: 70,
        getActions: ({ row }: GridRowParams<AccountDeletionRequest>) => [
          <GridActionsCellItem
            key={`account-deletion-${row.id}`}
            icon={<VisibilityOutlinedIcon />}
            label="Open"
            onClick={() => setOpened(row)}
          />,
        ],
      },
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'userId',
        headerName: 'User',
        width: 120,
        // The link opens the user in a new tab; it must not also open the request.
        renderCell: ({ row }) => (
          <Box onClick={(event) => event.stopPropagation()}>
            <FakeButtonLink startIcon={<OpenInNewIcon />} href={locations.user(row.userId)}>
              {row.userId}
            </FakeButtonLink>
          </Box>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: ({ row }) => <StatusCell row={row} />,
      },
      {
        field: 'reasonCode',
        headerName: 'Reason',
        width: 170,
        renderCell: ({ value }) => <code>{value}</code>,
      },
      {
        field: 'createdAt',
        headerName: 'Submitted',
        width: 190,
        valueFormatter: ({ value }) => (value ? formatDateTime(value) : ''),
      },
      {
        field: 'reviewEndsAt',
        headerName: 'Review',
        width: 200,
        renderCell: ({ row }) => <ReviewCell row={row} />,
      },
      {
        field: 'startedAt',
        headerName: 'Started',
        width: 190,
        valueFormatter: ({ value }) => (value ? formatDateTime(value) : '—'),
      },
      {
        field: 'closedAt',
        headerName: 'Completed / cancelled',
        width: 200,
        valueGetter: ({ row }) => row.completedAt || row.cancelledAt,
        valueFormatter: ({ value }) => (value ? formatDateTime(value) : '—'),
      },
    ];

    return base.map((column) => ({ ...column, sortable: false }));
  }, []);

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 2 }}>
        <Typography variant="h4">Account Deletion Requests</Typography>
        <Typography color="text.secondary">
          Each request is reviewed while the account is frozen. Support can cancel it until Ops starts it; Ops then runs
          the delete-user runbook and marks it completed.
        </Typography>
      </Box>
      <Tabs value={view.key} onChange={onViewChange} variant="scrollable" sx={{ mb: 2 }}>
        {QUEUE_VIEWS.map(({ key, label }) => (
          <Tab key={key} value={key} label={label} />
        ))}
      </Tabs>
      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the requests.
        </Alert>
      )}
      <NowContext.Provider value={now}>
        <DataGridPremium
          autoHeight
          rows={data?.items || []}
          rowCount={data?.totalItems || 0}
          columns={columns}
          loading={isFetching}
          getRowHeight={() => 'auto'}
          getRowClassName={baseGetRowClassName}
          sx={baseGridSx}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={PAGE_SIZES}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          onRowClick={({ row }) => setOpened(row)}
        />
      </NowContext.Provider>
      <AccountDeletionDialog
        request={selected}
        now={now}
        canCancel={canCancel}
        canProcess={canProcess}
        onClose={() => setOpened(null)}
      />
    </Container>
  );
}
